import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Wand2, FilePlus2, Copy, FileSpreadsheet, Check,
  Sparkles, Layers, ShieldCheck, Shield, Gem,
} from 'lucide-react';

interface MatrixListRow { id: string; name: string; factors_count: number; }
interface Band { band: string; upper_exclusive: number | null; label: string; }

const DEFAULT_BANDS: Band[] = [
  { band: 'low', upper_exclusive: 25, label: 'Low Risk' },
  { band: 'medium', upper_exclusive: 50, label: 'Medium Risk' },
  { band: 'high', upper_exclusive: 75, label: 'High Risk' },
  { band: 'critical', upper_exclusive: null, label: 'Critical Risk' },
];

const STEPS = ['Type', 'Starting point', 'Bands', 'Review'] as const;
type Step = 0 | 1 | 2 | 3;
type MatrixType = 'custom' | 'abm' | 'jb';
type StartingPoint = 'blank' | 'abm_v3' | 'jb_preset' | 'clone';

const TYPE_ENGINE: Record<MatrixType, string> = { custom: 'jb_v1', abm: 'abm_site_v3', jb: 'jb_v1' };
const TYPE_DEFAULT_START: Record<MatrixType, StartingPoint> = { custom: 'blank', abm: 'abm_v3', jb: 'jb_preset' };

export const MatrixWizardPage: React.FC = () => {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>(0);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [matrixType, setMatrixType] = useState<MatrixType>('custom');
  const [startingPoint, setStartingPoint] = useState<StartingPoint>('blank');
  const [cloneFromId, setCloneFromId] = useState('');
  const [bands, setBands] = useState<Band[]>(DEFAULT_BANDS);
  const [existingMatrices, setExistingMatrices] = useState<MatrixListRow[]>([]);
  const [abmSeedFactorCount, setAbmSeedFactorCount] = useState<number | null>(null);

  const engine = TYPE_ENGINE[matrixType];
  const pickType = (t: MatrixType): void => { setMatrixType(t); setStartingPoint(TYPE_DEFAULT_START[t]); };

  // Mount-once preload of matrix list + ABM v3 seed summary.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    void (async () => {
      try {
        const [ms, seed] = await Promise.all([
          api.get<MatrixListRow[]>('/risk/matrices'),
          api.get<{ summary: { factors: number } }>('/risk/matrices/utils/abm-v3-seed'),
        ]);
        setExistingMatrices(ms.data);
        setAbmSeedFactorCount(seed.data.summary.factors);
      } catch (err) {
        console.warn('matrix-wizard preload failed', err);
        toast.error('Could not preload matrix data');
      }
    })();
  }, []);

  const canGoNext = useMemo(() => {
    if (step === 0) return name.trim().length >= 2;
    if (step === 1) {
      if (startingPoint === 'clone') return !!cloneFromId;
      return true;
    }
    if (step === 2) {
      if (!bands.length) return false;
      return bands.every((b) => b.band && b.label);
    }
    return true;
  }, [step, name, startingPoint, cloneFromId, bands]);

  const goNext = () => setStep((s) => (s < 3 ? ((s + 1) as Step) : s));
  const goBack = () => setStep((s) => (s > 0 ? ((s - 1) as Step) : s));

  const create = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim(),
        engine,
        starting_point: startingPoint,
        bands,
      };
      if (startingPoint === 'clone') payload.clone_from_id = cloneFromId;
      const r = await api.post<{ ok: boolean; id: string }>('/risk/matrices', payload);
      toast.success(`Matrix “${name}” created`);
      nav(`../${r.data.id}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Create failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="matrix-wizard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-raptor-navy/60 text-xs">
            <button className="hover:underline" onClick={() => nav('..')} data-testid="back-to-list">
              &larr; Risk matrices
            </button>
          </div>
          <h2 className="font-headline text-2xl text-raptor-navy tracking-wide mt-1 flex items-center gap-2">
            <Wand2 size={20} /> New matrix setup
          </h2>
          <p className="text-sm text-raptor-navy/60 font-body">Four short steps.</p>
        </div>
        <div className="flex items-center gap-2">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`text-xs px-3 py-1 rounded-full font-body ${
                i === step
                  ? 'bg-raptor-blue text-white font-semibold'
                  : i < step
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-raptor-neutral/60 text-raptor-navy/60'
              }`}
              data-testid={`wizard-step-indicator-${i}`}
            >
              {i < step ? <Check size={12} className="inline mr-1" /> : `${i + 1}. `}
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Step 0 — Basics + Type */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Name your matrix &amp; pick a type</CardTitle>
            <CardDescription>
              Give it a clear name so underwriters can find it later, then choose the scoring model.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Matrix name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Retail Chain, Fuel Stations, Jewellers"
                data-testid="wizard-name"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What kind of risks does this matrix score?"
                rows={2}
                data-testid="wizard-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Matrix type *</Label>
              <StartingCard
                icon={<Sparkles size={16} />}
                title="Custom — lookup based (recommended)"
                subtitle="Build your own categories, factors and answer→points scoring lookups. The most flexible engine for any risk."
                selected={matrixType === 'custom'}
                onSelect={() => pickType('custom')}
                testid="type-custom"
              />
              <StartingCard
                icon={<Shield size={16} />}
                title="ABM Site v3 — security preset"
                subtitle="Alarm / PSIRA / crime-grade / floor-type / cash-value scoring with cluster compounding."
                selected={matrixType === 'abm'}
                onSelect={() => pickType('abm')}
                testid="type-abm"
              />
              <StartingCard
                icon={<Gem size={16} />}
                title="Jeweller's Block — preset"
                subtitle="The approved Jeweller's Block rating (exposure, stock, security, operations, fire) as a ready-made starting point."
                selected={matrixType === 'jb'}
                onSelect={() => pickType('jb')}
                testid="type-jb"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 1 — Starting point */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Choose a starting point</CardTitle>
            <CardDescription>You can edit everything after creation — this only decides what the first version looks like.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {matrixType === 'abm' && (
              <StartingCard
                icon={<Sparkles size={16} />}
                title="Use ABM v3 defaults"
                subtitle={abmSeedFactorCount ? `${abmSeedFactorCount} factors ready-to-tune (recommended)` : 'ABM v3 factory defaults'}
                selected={startingPoint === 'abm_v3'}
                onSelect={() => setStartingPoint('abm_v3')}
                testid="starting-abm-v3"
              />
            )}
            {matrixType === 'jb' && (
              <StartingCard
                icon={<Gem size={16} />}
                title="Use the Jeweller's Block preset"
                subtitle="Seeds the full approved JB categories, factors, scoring lookups, bands and attention rules."
                selected={startingPoint === 'jb_preset'}
                onSelect={() => setStartingPoint('jb_preset')}
                testid="starting-jb-preset"
              />
            )}
            <StartingCard
              icon={<FilePlus2 size={16} />}
              title="Start blank"
              subtitle={matrixType === 'abm'
                ? 'Empty security rubric — add every factor and table yourself'
                : 'Empty rubric — you add every category, factor and scoring lookup yourself'}
              selected={startingPoint === 'blank'}
              onSelect={() => setStartingPoint('blank')}
              testid="starting-blank"
            />
            <StartingCard
              icon={<Copy size={16} />}
              title="Clone an existing matrix"
              subtitle="Copy factors, weights, bands from another matrix then edit"
              selected={startingPoint === 'clone'}
              onSelect={() => setStartingPoint('clone')}
              testid="starting-clone"
            >
              {startingPoint === 'clone' && (
                <div className="mt-2">
                  <Label className="text-xs">Source matrix</Label>
                  <Select
                    value={cloneFromId}
                    onChange={(e) => setCloneFromId(e.target.value)}
                    data-testid="clone-source-select"
                  >
                    <option value="">— choose —</option>
                    {existingMatrices.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.factors_count} factors)
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </StartingCard>
            <StartingCard
              icon={<FileSpreadsheet size={16} />}
              title="Import from Excel (.xlsx)"
              subtitle="Coming soon — upload a rating workbook to auto-populate categories, factors and scoring lookups"
              selected={false}
              onSelect={() => toast.info('Excel import is coming next — share your template and we’ll wire it up')}
              disabled
              testid="starting-excel"
            />
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Bands */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Band thresholds</CardTitle>
            <CardDescription>
              A normalised score (0–100) falls into the first band whose <i>upper exclusive</i> is greater than the score.
              Leave the last band’s upper as blank for “no ceiling”. You can always change these later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm font-body">
              <thead className="text-xs text-raptor-navy/60">
                <tr>
                  <th className="py-1 text-left">Key</th>
                  <th className="py-1 text-left">Label</th>
                  <th className="py-1 text-right">Upper exclusive</th>
                </tr>
              </thead>
              <tbody>
                {bands.map((b, i) => (
                  <tr key={i} className="border-t border-border/60">
                    <td className="py-1">
                      <Input
                        value={b.band}
                        className="h-7 text-xs font-tabular"
                        onChange={(e) => {
                          const nb = [...bands]; nb[i] = { ...b, band: e.target.value }; setBands(nb);
                        }}
                        data-testid={`wizard-band-key-${i}`}
                      />
                    </td>
                    <td className="py-1">
                      <Input
                        value={b.label}
                        className="h-7 text-xs"
                        onChange={(e) => {
                          const nb = [...bands]; nb[i] = { ...b, label: e.target.value }; setBands(nb);
                        }}
                        data-testid={`wizard-band-label-${i}`}
                      />
                    </td>
                    <td className="py-1">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={b.upper_exclusive ?? ''}
                        placeholder="no ceiling"
                        className="h-7 text-xs font-tabular text-right"
                        onChange={(e) => {
                          const nb = [...bands];
                          nb[i] = { ...b, upper_exclusive: e.target.value === '' ? null : Number(e.target.value) };
                          setBands(nb);
                        }}
                        data-testid={`wizard-band-upper-${i}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Review */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ShieldCheck size={16} /> Review & create</CardTitle>
            <CardDescription>
              Confirm the setup. On “Create matrix” we’ll publish v1 immutably and drop you into the editor to tune the factors, weights and point tables.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm font-body">
            <ReviewLine label="Name" value={name} />
            {description && <ReviewLine label="Description" value={description} />}
            <ReviewLine label="Type" value={
              matrixType === 'custom' ? 'Custom — lookup based'
                : matrixType === 'abm' ? 'ABM Site v3 — security'
                : "Jeweller's Block"
            } />
            <ReviewLine label="Engine" value={<Badge variant="blue">{engine}</Badge>} />
            <ReviewLine
              label="Starting point"
              value={
                startingPoint === 'abm_v3'
                  ? `ABM v3 defaults (${abmSeedFactorCount ?? '?'} factors)`
                  : startingPoint === 'jb_preset'
                  ? "Jeweller's Block preset"
                  : startingPoint === 'clone'
                  ? `Cloned from ${existingMatrices.find((x) => x.id === cloneFromId)?.name || cloneFromId}`
                  : 'Blank (build from scratch)'
              }
            />
            <ReviewLine
              label="Bands"
              value={
                <div className="flex flex-wrap gap-1">
                  {bands.map((b) => (
                    <Badge key={b.band} variant="muted" className="text-[10px]">
                      {b.label} {b.upper_exclusive == null ? '(no ceiling)' : `< ${b.upper_exclusive}`}
                    </Badge>
                  ))}
                </div>
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Nav footer */}
      <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-border/60 bg-white/95 backdrop-blur px-6 py-3 flex items-center gap-2 justify-between" data-testid="wizard-footer">
        <Button variant="ghost" onClick={goBack} disabled={step === 0} data-testid="wizard-back">
          <ChevronLeft size={14} className="mr-1" /> Back
        </Button>
        {step < 3 ? (
          <Button variant="gradient" onClick={goNext} disabled={!canGoNext} data-testid="wizard-next">
            Next <ChevronRight size={14} className="ml-1" />
          </Button>
        ) : (
          <Button variant="gradient" onClick={() => void create()} disabled={saving} data-testid="wizard-create">
            {saving ? 'Creating…' : <><Layers size={14} className="mr-1" /> Create matrix</>}
          </Button>
        )}
      </div>
    </div>
  );
};

const StartingCard: React.FC<{
  icon: React.ReactNode; title: string; subtitle: string;
  selected: boolean; onSelect: () => void; disabled?: boolean;
  testid?: string; children?: React.ReactNode;
}> = ({ icon, title, subtitle, selected, onSelect, disabled, testid, children }) => (
  <div
    onClick={disabled ? undefined : onSelect}
    role="button"
    tabIndex={disabled ? -1 : 0}
    onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onSelect(); }}
    data-testid={testid}
    className={`p-3 rounded-md border transition-colors ${
      disabled
        ? 'opacity-50 cursor-not-allowed border-border/60'
        : selected
        ? 'border-raptor-blue bg-raptor-blue/5 cursor-pointer'
        : 'border-border/60 hover:bg-raptor-neutral/40 cursor-pointer'
    }`}
  >
    <div className="flex items-start gap-3">
      <div className={`mt-0.5 ${selected ? 'text-raptor-blue' : 'text-raptor-navy/60'}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-raptor-navy">{title}</div>
        <div className="text-xs text-raptor-navy/60 font-body">{subtitle}</div>
        {children}
      </div>
      {selected && !disabled && <Check className="text-raptor-blue shrink-0" size={16} />}
    </div>
  </div>
);

const ReviewLine: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-32 text-raptor-navy/60 text-xs font-body pt-0.5">{label}</div>
    <div className="flex-1 min-w-0">{typeof value === 'string' ? <span className="text-raptor-navy">{value}</span> : value}</div>
  </div>
);

export default MatrixWizardPage;
